import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Storage } from '../../Firebase/firebase';
import { ToastContainer, toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createClub, addbusinessToClub } from '../../../_services';
import './AddClubFinal.css';

const AddClubFinal = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const formRef = useRef();
    const [loading, setLoading] = useState(false);
    const [clubType, setClubType] = useState('public');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [smallLogoUrl, setSmallLogoUrl] = useState('');
    const [largeLogoUrl, setLargeLogoUrl] = useState('');
    const [smallLogoPreview, setSmallLogoPreview] = useState('');
    const [largeLogoPreview, setLargeLogoPreview] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);

    // Get current language direction
    const isRTL = i18n.language === 'he';

    // Check for business ID on component mount
    useEffect(() => {
        const checkBusinessId = async () => {
            const userData = localStorage.getItem('user');
            if (!userData) {
                toast.error(t('businessIdNotFound'));
                navigate('/');
                return;
            }

            try {
                const parsedUserData = JSON.parse(userData);
                if (!parsedUserData.business_id) {
                    // Wait a bit and try again
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    const updatedUserData = localStorage.getItem('user');
                    const updatedParsedData = JSON.parse(updatedUserData);
                    if (!updatedParsedData.business_id) {
                        toast.error(t('businessIdNotFound'));
                        navigate('/');
                        return;
                    }
                }
                setIsInitialized(true);
            } catch (error) {
                console.error('Error parsing user data:', error);
                toast.error(t('businessIdNotFound'));
                navigate('/');
            }
        };

        checkBusinessId();
    }, [navigate, t]);

    const handleImageUpload = async (file, type) => {
        if (!file) return;

        const img = new Image();
        img.src = URL.createObjectURL(file);

        return new Promise((resolve, reject) => {
            img.onload = async () => {
                const { naturalWidth, naturalHeight } = img;

                if (type === 'small' && naturalHeight <= 40) {
                    toast.error(t('invalidSmallLogo'));
                    reject(new Error('Invalid small logo dimensions'));
                    return;
                }

                if (type === 'large' && (naturalWidth <= 79 || naturalHeight <= 79)) {
                    toast.error(t('invalidLargeLogo'));
                    reject(new Error('Invalid large logo dimensions'));
                    return;
                }

                try {
                    const storageRef = ref(Storage, `/logos/${type}/${file.name}`);
                    await uploadBytes(storageRef, file);
                    const url = await getDownloadURL(storageRef);
                    resolve(url);
                } catch (error) {
                    console.error('Error uploading image:', error);
                    toast.error(t('imageUploadFailed'));
                    reject(error);
                }
            };
        });
    };

    const handleSmallLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            const url = await handleImageUpload(file, 'small');
            setSmallLogoUrl(url);
            setSmallLogoPreview(URL.createObjectURL(file));
            toast.success(t('smallLogoUploaded'));
        } catch (error) {
            console.error('Error uploading small logo:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLargeLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            const url = await handleImageUpload(file, 'large');
            setLargeLogoUrl(url);
            setLargeLogoPreview(URL.createObjectURL(file));
            toast.success(t('largeLogoUploaded'));
        } catch (error) {
            console.error('Error uploading large logo:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitOfExistClub = async (e) => {
        e.preventDefault();

        if (!isInitialized) {
            toast.error(t('pleaseWait'));
            return;
        }

        
        setLoading(true);
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            //const clubBusinessmember = localStorage.getItem("clubBusinessMember");
            if (!userData || !userData.business_id) {
                throw new Error(t('businessIdNotFound'));
            }
            
            const addBusinessResponse = await addbusinessToClub({ id: userData.business_id });
            const result = JSON.parse(addBusinessResponse);

            if (!result) {
                throw new Error(t('addBusinessFailed'));
            }

            toast.success(t('clubJoined'));
            navigate(-1);
        } catch (error) {
            console.error('Error joining to exist club:', error);
            toast.error(error.message || t('clubJoinFailed'));
        } finally {
            setLoading(false);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isInitialized) {
            toast.error(t('pleaseWait'));
            return;
        }

        if (!clubType) {
            toast.error(t('clubTypeRequired'));
            return;
        }

        if (!phoneNumber) {
            toast.error(t('phoneRequired'));
            return;
        }

        if (!formRef.current.terms.checked) {
            toast.error(t('termsRequired'));
            return;
        }

        setLoading(true);
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            if (!userData || !userData.business_id) {
                throw new Error(t('businessIdNotFound'));
            }

            const formData = {
                name: formRef.current.clubName.value,
                email: formRef.current.email.value,
                phoneNum: phoneNumber,
                type: clubType,
                description: formRef.current.description.value,
                website: formRef.current.website.value,
                extraInfo: formRef.current.extraInfo.value,
                smallLogoUrl,
                largeLogoUrl
            };

            const clubResponse = await createClub(formData);
            const club = JSON.parse(clubResponse);

            if (!club || Object.keys(club).length === 0) {
                throw new Error(t('clubCreationFailed'));
            }

            localStorage.setItem('clubId', JSON.stringify(club));

            const addBusinessResponse = await addbusinessToClub({ id : userData.business_id });
            const result = JSON.parse(addBusinessResponse);

            if (!result) {
                throw new Error(t('addBusinessFailed'));
            }

            toast.success(t('clubCreated'));
            navigate(-1);
        } catch (error) {
            console.error('Error creating club:', error);
            toast.error(error.message || t('clubCreationFailed'));
        } finally {
            setLoading(false);
        }
    };

    if (!isInitialized) {
        return (
            <div className="add-club-container" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="add-club-form-container">
                    <div className="add-club-form-header">
                        <h1>{t('loading')}</h1>
                        <p>{t('pleaseWait')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="add-club-page-container" dir={isRTL ? 'rtl' : 'ltr'}>
            <ToastContainer />
            <div className="add-club-container" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="add-club-form-container">
                    <div className="form-card-accent"></div>
                    <div className="add-club-form-header">
                        <h1>{t('createClub')}</h1>
                        <p>{t('createClubDescription')}</p>
                    </div>

                    <form ref={formRef} onSubmit={handleSubmit} className="add-club-form">
                        <div className="add-club-form-content">
                            <label className="add-club-type-label">
                                {t('selectClubType')}
                            </label>
                            <div className="add-club-type-buttons">
                                <button
                                    type="button"
                                    className={`add-club-type-button ${clubType === 'public' ? 'active' : ''}`}
                                    onClick={() => setClubType('public')}
                                >
                                    {t('public')}
                                </button>
                                <button
                                    type="button"
                                    className={`add-club-type-button ${clubType === 'private' ? 'active' : ''}`}
                                    onClick={() => setClubType('private')}
                                >
                                    {t('private')}
                                </button>
                            </div>

                            <div className="add-club-form-row">
                                <div className="add-club-form-group">
                                    <label className="add-club-form-label">
                                        {t('clubName')}
                                    </label>
                                    <input
                                        name="clubName"
                                        type="text"
                                        required
                                        className="add-club-form-input"
                                        placeholder={t('clubNamePlaceholder')}
                                    />
                                </div>

                                <div className="add-club-form-group">
                                    <label className="add-club-form-label">
                                        {t('email')}
                                    </label>
                                    <input
                                        name="email"
                                        type="email"
                                        required
                                        className="add-club-form-input"
                                        placeholder={t('emailPlaceholder')}
                                    />
                                </div>
                            </div>

                            <div className="add-club-form-row">
                                <div className="add-club-form-group">
                                    <label className="add-club-form-label">
                                        {t('phoneNumber')}
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder={t('phoneNumberPlaceholder')}
                                        className="add-club-form-input"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                <div className="add-club-form-group">
                                    <label className="add-club-form-label">
                                        {t('website')}
                                    </label>
                                    <input
                                        name="website"
                                        type="url"
                                        required
                                        className="add-club-form-input"
                                        placeholder={t('websitePlaceholder')}
                                    />
                                </div>
                            </div>

                            <div className="add-club-form-group">
                                <label className="add-club-form-label">
                                    {t('description')}
                                </label>
                                <input
                                    name="description"
                                    type="text"
                                    required
                                    className="add-club-form-input"
                                    placeholder={t('descriptionPlaceholder')}
                                />
                            </div>

                            <div className="add-club-form-group">
                                <label className="add-club-form-label">
                                    {t('extraInfo')}
                                </label>
                                <input
                                    name="extraInfo"
                                    type="text"
                                    required
                                    className="add-club-form-input"
                                    placeholder={t('extraInfoPlaceholder')}
                                />
                            </div>

                            <div className="add-club-logo-upload-container">
                                <div className="add-club-logo-upload">
                                    <label className="add-club-form-label">{t('smallLogo')}</label>
                                    <div className="add-club-logo-preview">
                                        {smallLogoPreview ? (
                                            <img src={smallLogoPreview} alt="Small logo preview" />
                                        ) : (
                                            <span>{t('noImageSelected')}</span>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleSmallLogoChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="add-club-logo-upload">
                                    <label className="add-club-form-label">{t('largeLogo')}</label>
                                    <div className="add-club-logo-preview">
                                        {largeLogoPreview ? (
                                            <img src={largeLogoPreview} alt="Large logo preview" />
                                        ) : (
                                            <span>{t('noImageSelected')}</span>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLargeLogoChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="add-club-terms-group">
                                <input
                                    type="checkbox"
                                    name="terms"
                                    className="add-club-terms-checkbox"
                                    required
                                />
                                <span className="add-club-terms-text">
                                    {t("iAccept")}
                                    <a href="#" className="terms-link">{t("termsOfService")}</a>
                                    <span> {t("&")} </span>
                                    <a href="#" className="terms-link">{t("privacyPolicy")}</a>
                                </span>
                            </div>

                            <button
                                type="submit"
                                className="add-club-submit-button"
                                disabled={loading}
                            >
                                {loading ? t('creating') : t('create')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddClubFinal;
